import { getPreWriteSignedWriteUrl, getPreReadSignedWriteUrl } from './s3Services';
import { getSlugName } from '../Components/helper';
import { apiClient, apiEndpoint } from './api.services';
import { API } from '../constants/api.constants';
import { toasterMsg } from '../Components/Toaster/Toaster';

/**
 * Upload design detail image to S3
 * @param {File} file - Image file to upload
 * @param {string} devOrderId - Development order ID
 * @param {number} designIndex - Design index (0-based)
 * @param {number} imageIndex - Image index within design (0-based)
 * @param {Function} onProgress - Progress callback (optional)
 * @returns {Promise<{s3Key: string, presignedUrl: string}>} - S3 key and presigned read URL
 */
export const uploadDesignImageToS3 = async (file, devOrderId, designIndex, imageIndex, onProgress) => {
  try {
    const companyURL = getSlugName();
    const timestamp = Date.now();
    const fileExtension = file.name?.split('.').pop()?.toLowerCase() || 'jpg';
    const s3Key = `${companyURL}/development-order/${devOrderId}/designs/do_design_${designIndex}_img_${imageIndex}_${timestamp}.${fileExtension}`;

    // Get presigned write URL for upload
    const presignedWriteUrlResponse = await getPreWriteSignedWriteUrl({
      objectKey: s3Key,
      contentType: file.type || `image/${fileExtension}`,
      expiresInSeconds: 3600,
    });

    if (presignedWriteUrlResponse?.code !== 200 || !presignedWriteUrlResponse?.data) {
      throw new Error('Failed to get presigned write URL');
    }

    const presignedWriteUrl = presignedWriteUrlResponse.data;

    // Upload file to S3 using XMLHttpRequest for progress tracking
    const uploadPromise = new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          const percentCompleted = Math.round((event.loaded / event.total) * 100);
          onProgress(percentCompleted);
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`S3 upload failed: ${xhr.statusText}`));
        }
      };

      xhr.onerror = () => {
        reject(new Error('S3 upload failed: Network error'));
      };

      xhr.open('PUT', presignedWriteUrl);
      xhr.setRequestHeader('Content-Type', file.type || `image/${fileExtension}`);
      xhr.send(file);
    });

    await uploadPromise;

    // Get presigned read URL for display
    // Ensure s3Key is a valid string
    if (!s3Key || typeof s3Key !== 'string' || s3Key.trim() === '') {
      throw new Error(`Invalid S3 key: must be a non-empty string. Received: ${typeof s3Key} - ${s3Key}`);
    }

    const presignedReadUrlResponse = await getPreReadSignedWriteUrl({
      objectKey: String(s3Key), // Explicitly convert to string
      expiresInSeconds: 3600,
    });

    if (presignedReadUrlResponse?.code !== 200 || !presignedReadUrlResponse?.data) {
      throw new Error('Failed to get presigned read URL');
    }

    const presignedReadUrl = presignedReadUrlResponse.data;

    return {
      s3Key,
      presignedUrl: presignedReadUrl,
    };
  } catch (error) {
    console.error('Error uploading design image to S3:', error);
    throw error;
  }
};

/**
 * Mark a design as default image
 * @param {string} id - Development order ID
 * @param {Object} data - Data object with designId
 * @returns {Promise<Object>} - API response
 */
export const markDefaultDesign = async (id, data) => {
  try {
    return await apiClient.post(`${apiEndpoint}${API.DESIGN_DETAILS.FIRST_PART}/${id}${API.DESIGN_DETAILS.MARK_DEFAULT}`, { ...data });
  } catch (error) {
    toasterMsg(`${error}`, 'danger');
    return null;
  }
};

/**
 * Save design sequence (reorder designs)
 * @param {string} id - Development order ID
 * @param {Object} data - Data object with designs array, comments, isComplete
 * @returns {Promise<Object>} - API response
 */
export const saveDesignSequence = async (id, data) => {
  try {
    return await apiClient.post(`${apiEndpoint}${API.DESIGN_DETAILS.FIRST_PART}/${id}${API.DESIGN_DETAILS.SAVE_SEQUENCE}`, { ...data });
  } catch (error) {
    toasterMsg(`${error}`, 'danger');
    return null;
  }
};

/**
 * Create a new design
 * @param {string} id - Development order ID
 * @param {Object} data - Data object with newDesign
 * @returns {Promise<Object>} - API response
 */
export const createNewDesign = async (id, data) => {
  try {
    return await apiClient.post(`${apiEndpoint}${API.DESIGN_DETAILS.FIRST_PART}/${id}${API.DESIGN_DETAILS.CREATE}`, { ...data });
  } catch (error) {
    toasterMsg(`${error}`, 'danger');
    return null;
  }
};

/**
 * Save image to a specific design detail slot
 * @param {string} devOrderId - Development order ID
 * @param {string} designId - Design detail ID
 * @param {Object} data - Data object with imageIndex, s3Key, imageTitle, imageDescription
 * @returns {Promise<Object>} - API response
 */
export const saveDesignImage = async (devOrderId, designId, data) => {
  try {
    return await apiClient.post(`${apiEndpoint}${API.DESIGN_DETAILS.FIRST_PART}/${devOrderId}${API.DESIGN_DETAILS.SAVE_IMAGE}/${designId}/save-image`, {
      ...data,
    });
  } catch (error) {
    toasterMsg(`${error}`, 'danger');
    return null;
  }
};

/**
 * Save image sequence for a design detail
 * @param {string} devOrderId - Development order ID
 * @param {string} designId - Design detail ID
 * @param {Array} images - Array of images in the new sequence order
 * @returns {Promise<Object>} - API response
 */
export const saveDesignImageSequence = async (devOrderId, designId, images, isSwap = false) => {
  try {
    return await apiClient.post(
      `${apiEndpoint}${API.DESIGN_DETAILS.FIRST_PART}/${devOrderId}${API.DESIGN_DETAILS.SAVE_IMAGE_SEQUENCE}/${designId}/save-image-sequence`,
      {
        images,
        isSwap,
      },
    );
  } catch (error) {
    toasterMsg(`${error}`, 'danger');
    return null;
  }
};

/**
 * Save comment for design details
 * @param {string} devOrderId - Development order ID
 * @param {Object} data - Data object with comments
 * @returns {Promise<Object>} - API response
 */
export const saveDesignComment = async (devOrderId, data) => {
  try {
    return await apiClient.post(`${apiEndpoint}${API.DESIGN_DETAILS.FIRST_PART}/${devOrderId}${API.DESIGN_DETAILS.SAVE_COMMENT}`, { ...data });
  } catch (error) {
    toasterMsg(`${error}`, 'danger');
    return null;
  }
};

/**
 * Save the per-design PDF-excluded rich-text comment
 * @param {string} devOrderId - Development order ID
 * @param {string} designId - Design ID
 * @param {Object} data - Data object with `comment` (HTML string)
 * @returns {Promise<Object>} - API response
 */
export const saveDesignPdfExcludedComment = async (devOrderId, designId, data) => {
  try {
    return await apiClient.post(
      `${apiEndpoint}${API.DESIGN_DETAILS.FIRST_PART}/${devOrderId}/design-details/${designId}${API.DESIGN_DETAILS.SAVE_PDF_EXCLUDED_COMMENT}`,
      { ...data },
    );
  } catch (error) {
    toasterMsg(`${error}`, 'danger');
    return null;
  }
};

/**
 * Delete a design detail
 * @param {string} devOrderId - Development order ID
 * @param {Object} data - Data object with designId
 * @returns {Promise<Object>} - API response
 */
export const deleteDesignDetail = async (devOrderId, data) => {
  try {
    return await apiClient.delete(`${apiEndpoint}${API.DESIGN_DETAILS.FIRST_PART}/${devOrderId}${API.DESIGN_DETAILS.DELETE}`, { data });
  } catch (error) {
    toasterMsg(`${error}`, 'danger');
    return null;
  }
};

/**
 * Delete multiple design details
 * @param {string} devOrderId - Development order ID
 * @param {Object} data - Data object with designIds array
 * @returns {Promise<Object>} - API response
 */
export const deleteMultipleDesignDetails = async (devOrderId, data) => {
  try {
    return await apiClient.delete(`${apiEndpoint}${API.DESIGN_DETAILS.FIRST_PART}/${devOrderId}${API.DESIGN_DETAILS.BULK_DELETE}`, { data });
  } catch (error) {
    toasterMsg(`${error}`, 'danger');
    return null;
  }
};

/**
 * Delete a single image from design
 * @param {string} devOrderId - Development order ID
 * @param {string} designId - Design ID
 * @param {string} imageId - Image ID to delete
 * @returns {Promise<Object>} - API response
 */
export const deleteDesignImage = async (devOrderId, designId, imageId) => {
  try {
    return await apiClient.delete(
      `${apiEndpoint}${API.DESIGN_DETAILS.FIRST_PART}/${devOrderId}${API.DESIGN_DETAILS.DELETE_IMAGE}/${designId}/delete-image/${imageId}`,
    );
  } catch (error) {
    toasterMsg(`${error}`, 'danger');
    return null;
  }
};

export const unArchiveDesignDetail = async (data) => {
  try {
    return await apiClient.post(`${apiEndpoint}${API.DESIGN_DETAILS.FIRST_PART}/${API.DESIGN_DETAILS.UNARCHIVE_REPORT}`, { ...data });
  } catch (error) {
    toasterMsg(`${error}`, 'danger');
    return null;
  }
};

/**
 * Upload cropped image (base64) to S3
 * @param {string} base64Image - Base64 cropped image string
 * @param {string} devOrderId - Development order ID
 * @param {string} designId - Design ID
 * @param {Function} onProgress - Progress callback (optional)
 * @returns {Promise<string>} - S3 key
 */
export const uploadCroppedImageToS3 = async (base64Image, devOrderId, designId, onProgress) => {
  try {
    const companyURL = getSlugName();
    const timestamp = Date.now();
    const s3Key = `${companyURL}/development-order/${devOrderId}/designs/do_design_${designId}_cropped_${timestamp}.jpg`;

    const presignedWriteUrlResponse = await getPreWriteSignedWriteUrl({
      objectKey: s3Key,
      contentType: 'image/jpeg',
      expiresInSeconds: 3600,
    });

    if (presignedWriteUrlResponse?.code !== 200 || !presignedWriteUrlResponse?.data) {
      throw new Error('Failed to get presigned write URL');
    }

    const presignedWriteUrl = presignedWriteUrlResponse.data;

    const base64Data = base64Image.split(',')[1];
    const binaryString = atob(base64Data);
    const len = binaryString.length;
    const buffer = new Uint8Array(len);

    for (let i = 0; i < len; i++) {
      buffer[i] = binaryString.charCodeAt(i);
    }

    const uploadPromise = new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          const percentCompleted = Math.round((event.loaded / event.total) * 100);
          onProgress(percentCompleted);
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(s3Key);
        } else {
          reject(new Error(`S3 upload failed: ${xhr.statusText}`));
        }
      };

      xhr.onerror = () => {
        reject(new Error('S3 upload failed: Network error'));
      };

      xhr.open('PUT', presignedWriteUrl);
      xhr.setRequestHeader('Content-Type', 'image/jpeg');
      xhr.send(buffer);
    });

    await uploadPromise;
    return s3Key;
  } catch (error) {
    console.error('Error uploading cropped image to S3:', error);
    throw error;
  }
};

/**
 * Crop default design image
 * @param {string} devOrderId - Development order ID
 * @param {Object} data - Data object with _id and croppedImage (S3 key)
 * @returns {Promise<Object>} - API response
 */
export const cropDefaultDesign = async (devOrderId, data) => {
  try {
    return await apiClient.post(`${apiEndpoint}${API.DESIGN_DETAILS.FIRST_PART}/${devOrderId}${API.DESIGN_DETAILS.CROP_DEFAULT_DESIGN}`, { ...data });
  } catch (error) {
    toasterMsg(`${error}`, 'danger');
    return null;
  }
};
